// Group Payroll Overview — every company the viewer can see, side by side for one pay
// period: run status, headcount, totals, and approvals still pending. Built on the pure
// buildGroupOverview (api/groupOverview.ts), which keeps the rules: withheld figures are
// "Restricted" and never summed; a company without a run says so; a load failure is an
// error, never an empty group.
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { AlertTriangle, ChevronRight, Info, Loader2, RefreshCw } from 'lucide-react';
import { Button } from '@/features/shared/components/ui/button';
import { Card, CardContent } from '@/features/shared/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/features/shared/components/ui/table';
import { getApiErrorMessage } from '@/lib/apiError';
import { formatMoney } from '../lib/format';
import { RUN_STATE_META } from '../lib/state';
import { StatusChip } from './StatusChip';
import { RestrictedValue } from './RestrictedValue';
import {
  buildGroupOverview,
  getOverviewApprovals,
  listOverviewRuns,
  listVisibleCompanies,
  overviewPeriods,
  periodKey,
  type Figure,
  type OverviewApproval,
  type OverviewCompany,
  type OverviewRunView,
  type PendingApprovals,
} from '../api/groupOverview';
import type { Currency, RunState } from '../types';

const periodName = (key: string) => {
  const [y, m] = key.split('-').map(Number);
  if (!y || !m) return key;
  return new Date(y, m - 1, 1).toLocaleDateString(undefined, { month: 'long', year: 'numeric' });
};

const FigureCell: React.FC<{ figure: Figure | null; currency: string | null }> = ({ figure, currency }) => {
  if (!figure) return <span className="text-muted-foreground">—</span>;
  if (figure.kind === 'restricted') return <RestrictedValue />;
  return <>{formatMoney(figure.amount, (currency ?? '') as Currency)}</>;
};

const PendingCell: React.FC<{ p: PendingApprovals; error?: string; onRetry?: () => void }> = ({ p, error, onRetry }) => {
  switch (p.kind) {
    case 'not_submitted':
      return <span className="text-muted-foreground">Not submitted</span>;
    case 'none':
      return <span className="text-muted-foreground">None pending</span>;
    case 'loading':
      return (
        <span className="inline-flex items-center gap-1 text-muted-foreground">
          <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden /> Checking…
        </span>
      );
    case 'unknown':
      return (
        <span
          className="inline-flex items-center gap-1 text-warning-strong"
          title={error ? `The approval chain could not be loaded: ${error}` : 'The approval chain could not be loaded'}
        >
          <AlertTriangle className="h-3.5 w-3.5" /> Couldn't load
          {onRetry && (
            <button
              type="button"
              className="ml-1 underline underline-offset-2"
              onClick={(e) => {
                e.stopPropagation();
                onRetry();
              }}
            >
              Retry
            </button>
          )}
        </span>
      );
    case 'pending':
      return (
        <span className="text-foreground">
          {p.count} pending · next: {p.next}
          {p.nextIsGroup && (
            <span className="ml-1.5 rounded bg-info-soft px-1.5 py-0.5 text-[10px] font-medium text-info-strong">Group Finance</span>
          )}
        </span>
      );
  }
};

type Load =
  | { status: 'loading' }
  | { status: 'error'; message: string }
  | { status: 'ready'; companies: OverviewCompany[]; runs: OverviewRunView[] };

export const GroupPayrollOverview: React.FC<{ onOpenRun: (runId: string) => void }> = ({ onOpenRun }) => {
  const [load, setLoad] = useState<Load>({ status: 'loading' });
  const [period, setPeriod] = useState('');
  const [approvals, setApprovals] = useState<Record<string, OverviewApproval[] | 'error'>>({});
  // Why a run's chain failed (shown on hover), keyed by run id.
  const [approvalErrors, setApprovalErrors] = useState<Record<string, string>>({});
  // Chains already requested. A ref, not state: the fetch below must not be cancelled
  // (and its answer dropped) just because another run's answer changed `approvals`.
  const requested = useRef(new Set<string>());

  const fetchAll = useCallback(async () => {
    setLoad({ status: 'loading' });
    // A full refresh re-asks for every chain too ("Try again" must retry those as well).
    requested.current.clear();
    setApprovals({});
    setApprovalErrors({});
    try {
      const [companies, runs] = await Promise.all([listVisibleCompanies(), listOverviewRuns()]);
      setLoad({ status: 'ready', companies, runs });
      setPeriod((cur) => cur || overviewPeriods(runs)[0] || '');
    } catch (e) {
      setLoad({ status: 'error', message: getApiErrorMessage(e, 'The group overview could not be loaded.') });
    }
  }, []);

  useEffect(() => {
    void fetchAll();
  }, [fetchAll]);

  const periods = useMemo(() => (load.status === 'ready' ? overviewPeriods(load.runs) : []), [load]);

  // Approval chains for runs awaiting approval in the selected period (best-effort per run:
  // a failure marks THAT run "couldn't load", it never reads as "none pending"; until the
  // answer arrives the cell says "Checking…", never "Couldn't load").
  useEffect(() => {
    if (load.status !== 'ready' || !period) return;
    const missing = load.runs.filter(
      (r) =>
        periodKey(r.period_start) === period &&
        r.state === 'validated' &&
        approvals[r.id] === undefined &&
        !requested.current.has(r.id),
    );
    for (const r of missing) {
      requested.current.add(r.id);
      getOverviewApprovals(r.id)
        .then((a) => setApprovals((cur) => ({ ...cur, [r.id]: a })))
        .catch((e) => {
          setApprovals((cur) => ({ ...cur, [r.id]: 'error' }));
          setApprovalErrors((cur) => ({ ...cur, [r.id]: getApiErrorMessage(e, 'no response from the server') }));
        })
        .finally(() => requested.current.delete(r.id));
    }
  }, [load, period, approvals]);

  /** Ask again for one run's chain (back to "Checking…"; the effect refetches it). */
  const retryApprovals = useCallback((runId: string) => {
    setApprovals((cur) => {
      const next = { ...cur };
      delete next[runId];
      return next;
    });
  }, []);

  if (load.status === 'loading') {
    return (
      <div className="py-12 text-center text-muted-foreground">
        <Loader2 className="mr-2 inline h-4 w-4 animate-spin" /> Loading the group overview…
      </div>
    );
  }
  if (load.status === 'error') {
    return (
      <div role="alert" className="rounded-lg bg-danger-soft p-4 text-sm text-danger-strong">
        <p className="font-medium">The group overview could not be loaded</p>
        <p className="mt-0.5">{load.message}</p>
        <Button variant="outline" size="sm" className="mt-3 gap-2" onClick={() => void fetchAll()}>
          <RefreshCw className="h-4 w-4" /> Try again
        </Button>
      </div>
    );
  }
  if (load.companies.length === 0) {
    return (
      <div className="rounded-lg border border-border p-6 text-center text-sm text-muted-foreground">
        You don't have access to any company's payroll yet.
      </div>
    );
  }
  if (periods.length === 0) {
    return (
      <div className="rounded-lg border border-border p-6 text-center text-sm text-muted-foreground">
        No payroll runs yet for the {load.companies.length} companies you can see.
      </div>
    );
  }

  const { rows, summary } = buildGroupOverview(load.companies, load.runs, period, approvals);

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <label className="space-y-1.5" htmlFor="overview-period">
          <span className="text-xs font-medium text-muted-foreground">Pay period</span>
          <select
            id="overview-period"
            className="block rounded-md border border-input bg-background px-3 py-2 text-sm text-foreground"
            value={period}
            onChange={(e) => setPeriod(e.target.value)}
          >
            {periods.map((p) => (
              <option key={p} value={p}>
                {periodName(p)}
              </option>
            ))}
          </select>
        </label>
        <p className="text-sm text-muted-foreground" data-testid="overview-summary">
          {summary.companiesWithRun} of {summary.companies} companies have a run · {summary.runsAwaitingApproval} awaiting
          approval · {summary.pendingSteps} approval step{summary.pendingSteps === 1 ? '' : 's'} pending
          {summary.unknownRuns > 0 && ` (${summary.unknownRuns} couldn't load)`}
        </p>
      </div>

      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Company</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Employees</TableHead>
                <TableHead className="text-right">Gross</TableHead>
                <TableHead className="text-right">Net pay</TableHead>
                <TableHead className="text-right">Employer cost</TableHead>
                <TableHead>Approvals</TableHead>
                <TableHead className="w-10" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((row) => {
                const single = row.runs.length === 1 ? row.runs[0] : null;
                return (
                  <TableRow
                    key={row.company.id}
                    data-testid={`overview-row-${row.company.id}`}
                    className={single ? 'cursor-pointer' : undefined}
                    onClick={single ? () => onOpenRun(single.id) : undefined}
                  >
                    <TableCell className="font-medium text-charcoal">{row.company.name}</TableCell>
                    {row.runs.length === 0 ? (
                      <TableCell colSpan={6} className="text-muted-foreground">
                        {row.cancelledOnly ? 'Run cancelled — no active run for this period' : 'No run for this period'}
                      </TableCell>
                    ) : (
                      <>
                        <TableCell>
                          <div className="flex flex-col gap-1">
                            {row.runs.map((c) => (
                              <button
                                type="button"
                                key={c.id}
                                className="inline-flex items-center gap-1.5 text-left"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  onOpenRun(c.id);
                                }}
                              >
                                <StatusChip meta={RUN_STATE_META[c.state as RunState]} />
                                {c.label && <span className="text-xs text-muted-foreground">{c.label}</span>}
                              </button>
                            ))}
                          </div>
                        </TableCell>
                        <TableCell className="text-right tabular-nums">
                          {row.runs.map((c) => (c.headcount == null ? '—' : String(c.headcount))).join(' + ')}
                        </TableCell>
                        <TableCell className="text-right tabular-nums">
                          <FigureCell figure={row.gross} currency={row.currency} />
                        </TableCell>
                        <TableCell className="text-right font-medium tabular-nums">
                          <FigureCell figure={row.net} currency={row.currency} />
                        </TableCell>
                        <TableCell className="text-right tabular-nums">
                          <FigureCell figure={row.employerCost} currency={row.currency} />
                        </TableCell>
                        <TableCell>
                          <div className="flex flex-col gap-1 text-sm">
                            {row.runs.map((c) => (
                              <PendingCell
                                key={c.id}
                                p={c.pending}
                                error={approvalErrors[c.id]}
                                onRetry={() => retryApprovals(c.id)}
                              />
                            ))}
                          </div>
                        </TableCell>
                      </>
                    )}
                    <TableCell>{single && <ChevronRight className="h-4 w-4 text-muted-foreground" />}</TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <p className="flex items-start gap-2 text-xs text-muted-foreground">
        <Info className="mt-0.5 h-3.5 w-3.5 shrink-0" />
        Totals are shown per company, in its own currency. There is no group total: companies may pay in different
        currencies, and figures you don't have access to are shown as Restricted and never added up.
      </p>
    </div>
  );
};

export default GroupPayrollOverview;
